import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Avatar,
    Chip,
    Container,
    Menu,
    MenuItem as MuiMenuItem,
    Divider,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    TrendingUp as TrendingUpIcon,
    DateRange as DateRangeIcon,
    Store as StoreIcon,
    Assessment as AssessmentIcon,
    ChevronLeft as ChevronLeftIcon,
    LocationOn as LocationOnIcon,
    Brightness4 as DarkModeIcon,
    NotificationsOutlined as NotificationsIcon,
    AccountCircleOutlined as AccountIcon,
    Speed as SpeedIcon,
    Groups as GroupsIcon,
    People as PeopleIcon,
    Summarize as SummarizeIcon,
    ManageAccounts as ManageAccountsIcon,
    Logout as LogoutIcon,
    Person as PersonIcon,
    Settings as SettingsIcon,
    Analytics as AnalyticsIcon,
    Email as EmailIcon,
    Storage as StorageIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { glassStyle } from '../theme/luxuryTheme';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

interface LayoutProps {
    children: React.ReactNode;
}

interface MenuItem {
    text: string;
    path: string;
    icon: React.ReactElement;
}

const menuItems: MenuItem[] = [
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Executive Summary', path: '/executive', icon: <SummarizeIcon /> },
    { text: 'Advanced Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
    { text: 'Store Locations', path: '/stores', icon: <StoreIcon /> },
    // Hidden per user request
    // { text: 'Operations', path: '/operations', icon: <SpeedIcon /> },
    // Hidden until data is available
    // { text: 'Labor Analytics', path: '/labor', icon: <GroupsIcon /> },
    // { text: 'Customer Analytics', path: '/customers', icon: <PeopleIcon /> },
    { text: 'Daily Sales', path: '/daily-sales', icon: <TrendingUpIcon /> },
    { text: 'Date Range Report', path: '/date-range', icon: <DateRangeIcon /> },
    { text: 'Store Comparison', path: '/store-comparison', icon: <StoreIcon /> },
    { text: 'Locations', path: '/locations', icon: <LocationOnIcon /> },
    { text: 'Reports', path: '/reports', icon: <AssessmentIcon /> },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user, logout, hasRole } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavigate = (path: string) => {
        navigate(path);
        if (isMobile) {
            setMobileOpen(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
    };

    const handleUserManagement = () => {
        handleMenuClose();
        navigate('/users');
    };

    const handleReportRecipients = () => {
        handleMenuClose();
        navigate('/report-recipients');
    };

    const handleETLMonitoring = () => {
        handleMenuClose();
        navigate('/etl-monitoring');
    };

    const getUserInitials = () => {
        if (!user) return 'U';
        return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    };

    const getRoleColor = () => {
        if (!user) return 'default';
        const roleColors: Record<string, string> = {
            admin: '#ff4444',
            executive: '#ffa500',
            corporate: '#4169e1',
            franchise_owner: '#32cd32',
            store_manager: '#808080'
        };
        return roleColors[user.role] || '#808080';
    };

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 2, pt: 3, pb: 1, textAlign: 'center', flexShrink: 0 }}>
                <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
                    <Box
                        sx={{
                            width: 140,
                            height: 60,
                            margin: '0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src="/savvy-sliders-logo.webp"
                            alt="Savvy Sliders"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                </motion.div>
            </Box>

            <List sx={{ px: 2, pb: 2, flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
                <AnimatePresence>
                    {menuItems.map((item, index) => (
                        <motion.div
                            key={item.path}
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    selected={location.pathname === item.path}
                                    onClick={() => handleNavigate(item.path)}
                                    sx={{
                                        borderRadius: '12px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background:
                                                location.pathname === item.path
                                                    ? 'linear-gradient(135deg, rgba(175, 35, 38, 0.2) 0%, rgba(175, 35, 38, 0.1) 100%)'
                                                    : 'transparent',
                                            transition: 'all 0.3s ease',
                                        },
                                        '&:hover': {
                                            transform: 'translateX(5px)',
                                            '&::before': {
                                                background:
                                                    'linear-gradient(135deg, rgba(175, 35, 38, 0.1) 0%, rgba(175, 35, 38, 0.05) 100%),',
                                            },
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 600,
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 40,
                                            color:
                                                location.pathname === item.path ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontSize: '0.95rem',
                                            fontWeight: location.pathname === item.path ? 600 : 400,
                                        }}
                                    />
                                    {location.pathname === item.path && (
                                        <motion.div
                                            layoutId="activeIndicator"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                width: 4,
                                                height: '100%',
                                                background: 'linear-gradient(180deg, #af2326 0%, #c94144 100%)',
                                                borderRadius: '0 4px 4px 0',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </motion.div>
                    ))}
                    {hasRole(['admin', 'executive']) && (
                        <>
                            <motion.div
                                initial={{ x: -50, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: menuItems.length * 0.1 }}
                            >
                                <ListItem disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        selected={location.pathname === '/users'}
                                        onClick={() => handleNavigate('/users')}
                                        sx={{
                                            borderRadius: '12px',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background:
                                                    location.pathname === '/users'
                                                        ? 'linear-gradient(135deg, rgba(175, 35, 38, 0.2) 0%, rgba(175, 35, 38, 0.1) 100%)'
                                                        : 'transparent',
                                                transition: 'all 0.3s ease',
                                            },
                                            '&:hover': {
                                                transform: 'translateX(5px)',
                                                '&::before': {
                                                background:
                                                    'linear-gradient(135deg, rgba(175, 35, 38, 0.1) 0%, rgba(175, 35, 38, 0.05) 100%),',
                                            },
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 600,
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 40,
                                            color:
                                                location.pathname === '/users' ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <ManageAccountsIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="User Management"
                                        primaryTypographyProps={{
                                            fontSize: '0.95rem',
                                            fontWeight: location.pathname === '/users' ? 600 : 400,
                                        }}
                                    />
                                    {location.pathname === '/users' && (
                                        <motion.div
                                            layoutId="activeIndicator"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                width: 4,
                                                height: '100%',
                                                background: 'linear-gradient(180deg, #af2326 0%, #c94144 100%)',
                                                borderRadius: '0 4px 4px 0',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: (menuItems.length + 1) * 0.1 }}
                        >
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    selected={location.pathname === '/report-recipients'}
                                    onClick={() => handleNavigate('/report-recipients')}
                                    sx={{
                                        borderRadius: '12px',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background:
                                                location.pathname === '/report-recipients'
                                                    ? 'linear-gradient(135deg, rgba(175, 35, 38, 0.2) 0%, rgba(175, 35, 38, 0.1) 100%)'
                                                    : 'transparent',
                                            transition: 'all 0.3s ease',
                                        },
                                        '&:hover': {
                                            transform: 'translateX(5px)',
                                            '&::before': {
                                                background:
                                                    'linear-gradient(135deg, rgba(175, 35, 38, 0.1) 0%, rgba(175, 35, 38, 0.05) 100%),',
                                            },
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            '& .MuiListItemIcon-root': {
                                                color: theme.palette.primary.main,
                                            },
                                            '& .MuiListItemText-primary': {
                                                fontWeight: 600,
                                                color: theme.palette.primary.main,
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 40,
                                            color:
                                                location.pathname === '/report-recipients' ? theme.palette.primary.main : theme.palette.text.secondary,
                                        }}
                                    >
                                        <EmailIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Report Recipients"
                                        primaryTypographyProps={{
                                            fontSize: '0.95rem',
                                            fontWeight: location.pathname === '/report-recipients' ? 600 : 400,
                                        }}
                                    />
                                    {location.pathname === '/report-recipients' && (
                                        <motion.div
                                            layoutId="activeIndicator"
                                            style={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                width: 4,
                                                height: '100%',
                                                background: 'linear-gradient(180deg, #af2326 0%, #c94144 100%)',
                                                borderRadius: '0 4px 4px 0',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </List>

            <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.06)', flexShrink: 0 }}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Box
                        sx={{
                            p: 2,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, rgba(175, 35, 38, 0.1) 0%, rgba(175, 35, 38, 0.05) 100%)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                background: `linear-gradient(135deg, ${getRoleColor()} 0%, ${getRoleColor()}aa 100%)`,
                            }}
                        >
                            {getUserInitials()}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {user ? `${user.firstName} ${user.lastName}` : 'User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {user?.email || 'Not logged in'}
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    ...glassStyle,
                    backgroundColor: scrolled ? 'rgba(28, 24, 25, 0.95)' : 'rgba(28, 24, 25, 0.85)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    transition: 'all 0.3s ease',
                    zIndex: theme.zIndex.drawer + 1,
                }}
            >
                <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <img 
                                src="/savvy-sliders-logo.webp" 
                                alt="Savvy Sliders" 
                                style={{ 
                                    height: '32px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                }} 
                            />
                        </Box>
                        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
                            <Typography variant="h6" noWrap sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
                                {location.pathname === '/users'
                                    ? 'User Management'
                                    : location.pathname === '/report-recipients'
                                    ? 'Report Recipients'
                                    : location.pathname === '/etl-monitoring'
                                    ? 'ETL Monitoring'
                                    : location.pathname === '/analytics'
                                    ? 'Advanced Analytics'
                                    : menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
                            </Typography>
                        </motion.div>
                        <Chip
                            label="Live"
                            size="small"
                            sx={{
                                height: 20,
                                backgroundColor: 'rgba(175, 35, 38, 0.2)',
                                color: '#af2326',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                '& .MuiChip-label': { px: 1 },
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user && (
                            <Chip
                                label={user.role.replace('_', ' ').toUpperCase()}
                                size="small"
                                sx={{
                                    background: `linear-gradient(135deg, ${getRoleColor()} 0%, ${getRoleColor()}aa 100%)`,
                                    color: 'white',
                                    fontWeight: 500,
                                    mr: 1,
                                }}
                            />
                        )}
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    },
                                }}
                            >
                                <DarkModeIcon />
                            </IconButton>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    },
                                }}
                            >
                                <NotificationsIcon />
                            </IconButton>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Avatar
                                onClick={handleMenuOpen}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    ml: 1,
                                    background: `linear-gradient(135deg, ${getRoleColor()} 0%, ${getRoleColor()}aa 100%)`,
                                    cursor: 'pointer',
                                    border: '2px solid rgba(255, 255, 255, 0.12)',
                                }}
                            >
                                {getUserInitials()}
                            </Avatar>
                        </motion.div>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    backgroundColor: 'rgba(28, 24, 25, 0.95)',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    minWidth: 200,
                                },
                            }}
                        >
                            <Box sx={{ px: 2, py: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {user ? `${user.firstName} ${user.lastName}` : 'User'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {user?.email}
                                </Typography>
                            </Box>
                            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
                            {hasRole(['admin', 'executive']) && (
                                <>
                                    <MuiMenuItem onClick={handleUserManagement}>
                                        <ListItemIcon>
                                            <ManageAccountsIcon fontSize="small" />
                                        </ListItemIcon>
                                        User Management
                                    </MuiMenuItem>
                                    <MuiMenuItem onClick={handleReportRecipients}>
                                        <ListItemIcon>
                                            <EmailIcon fontSize="small" />
                                        </ListItemIcon>
                                        Report Recipients
                                    </MuiMenuItem>
                                    <MuiMenuItem onClick={handleETLMonitoring}>
                                        <ListItemIcon>
                                            <StorageIcon fontSize="small" />
                                        </ListItemIcon>
                                        ETL Monitoring
                                    </MuiMenuItem>
                                </>
                            )}
                            <MuiMenuItem onClick={handleMenuClose}>
                                <ListItemIcon>
                                    <SettingsIcon fontSize="small" />
                                </ListItemIcon>
                                Settings
                            </MuiMenuItem>
                            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
                            <MuiMenuItem onClick={handleLogout}>
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" />
                                </ListItemIcon>
                                Logout
                            </MuiMenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? mobileOpen : true}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        ...glassStyle,
                        backgroundColor: 'rgba(28, 24, 25, 0.95)',
                        borderRight: '1px solid rgba(255, 255, 255, 0.06)',
                        mt: { md: '64px' },
                        height: { md: 'calc(100vh - 64px)' },
                        overflow: 'hidden',
                    },
                }}
            >
                {drawer}
            </Drawer>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: 0 },
                    minHeight: '100vh',
                    pt: '64px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Container maxWidth="xl" sx={{ py: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <motion.div
                        key={location.pathname}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        style={{ minHeight: '400px', flex: 1 }}
                    >
                        {children || <div style={{ padding: '20px' }}>Loading...</div>}
                    </motion.div>
                </Container>
            </Box>
        </Box>
    );
};

export default Layout;
